import React from 'react';

import {getBufferedRanges} from './buffered';
import cs from './styles.module.scss';

interface Props {
  // 0 at the first frame, 1 at the last.
  fraction: number;
  // Whether each frame's image has loaded (or is known to be missing), in order.
  loaded: boolean[];
  label: string;
  onSeek: (fraction: number) => void;
}

const ProgressLine: React.FunctionComponent<Props> = ({fraction, loaded, label, onSeek}) => {
  const buffered = getBufferedRanges(loaded);

  const [isDragging, setIsDragging] = React.useState(false);

  const seekTo = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width) {
      onSeek(Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1));
    }
  };

  return (
    // The hit area is taller than the visible line so it's easy to click.
    <div
      className={cs.hitArea}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fraction * 100)}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setIsDragging(true);
        seekTo(e);
      }}
      onPointerMove={(e) => isDragging && seekTo(e)}
      onPointerUp={() => setIsDragging(false)}
      onPointerCancel={() => setIsDragging(false)}
    >
      <div className={cs.track}>
        {buffered.map(([start, end]) => (
          <div
            key={start}
            className={cs.buffered}
            style={{left: `${start * 100}%`, width: `${(end - start) * 100}%`}}
          />
        ))}
        <div className={cs.fill} style={{width: `${fraction * 100}%`}} />
      </div>
    </div>
  );
};

export default ProgressLine;
